import React, { useState } from 'react';
import { CheckIcon, XIcon, ShieldCheckIcon, AlertCircleIcon } from 'lucide-react';
import { mockData } from '../utils/mockData';
import { Button } from './common/Button';
import { motion, AnimatePresence } from 'framer-motion';
interface AffidavitModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}
export const AffidavitModal = ({
  isOpen,
  onClose,
  onConfirm
}: AffidavitModalProps) => {
  const [isChecked, setIsChecked] = useState(false);
  const {
    title,
    declarations,
    confirmationText
  } = mockData.affidavit;
  return <AnimatePresence data-id="element-29">
      {isOpen && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8" data-id="element-30">
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-command-app/95 backdrop-blur-xl" data-id="element-31" />

          {/* Modal Container */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="w-full max-w-[800px] bg-command-panel border border-command-border-soft rounded-[2rem] shadow-2xl relative overflow-hidden flex flex-col max-h-[90vh]" data-id="element-32">
            {/* Header */}
            <div className="p-8 pb-4 flex items-center justify-between" data-id="element-33">
              <div className="flex items-center gap-3" data-id="element-34">
                <div className="w-10 h-10 rounded-xl bg-[#4a90e2]/10 flex items-center justify-center text-[#4a90e2] border border-[#4a90e2]/20" data-id="element-35">
                  <ShieldCheckIcon size={24} data-id="element-36" />
                </div>
                <div data-id="element-37">
                  <h2 className="text-2xl font-bold text-white tracking-tight" data-id="element-38">
                    {title}
                  </h2>
                  <p className="text-xs font-bold text-command-text-muted uppercase tracking-[0.2em] mt-0.5" data-id="element-39">
                    Official Certification Required
                  </p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-command-input rounded-full text-command-text-muted hover:text-white transition-colors" data-id="element-40">
                <XIcon size={24} data-id="element-41" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-grow overflow-y-auto custom-scrollbar p-8 pt-4 space-y-8" data-id="element-42">
              <div className="bg-command-input/30 border border-command-border rounded-2xl p-6 space-y-6" data-id="element-43">
                <p className="text-lg font-medium text-white/90" data-id="element-44">
                  I, the undersigned officer, do hereby declare and affirm:
                </p>
                <div className="space-y-4" data-id="element-45">
                  {declarations.map((declaration, index) => <div key={index} className="flex gap-4 group" data-id="element-46">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-[10px] font-bold text-command-text-muted group-hover:border-[#4a90e2]/50 transition-colors" data-id="element-47">
                        {index + 1}
                      </div>
                      <p className="text-base text-command-text-muted leading-relaxed group-hover:text-white/90 transition-colors" data-id="element-48">
                        {declaration}
                      </p>
                    </div>)}
                </div>
              </div>

              <div className="flex items-start gap-4 p-6 bg-[#f59e0b]/5 border border-[#f59e0b]/20 rounded-2xl" data-id="element-49">
                <AlertCircleIcon size={20} className="text-[#f59e0b] flex-shrink-0 mt-0.5" data-id="element-50" />
                <p className="text-sm text-[#f59e0b]/80 leading-relaxed" data-id="element-51">
                  Submission of this report constitutes an official statement.
                  Any knowingly false entries are subject to administrative and
                  criminal penalties.
                </p>
              </div>

              <label className={`flex items-center gap-4 p-6 rounded-2xl cursor-pointer transition-all border ${isChecked ? 'bg-[#4a90e2]/10 border-[#4a90e2] shadow-lg shadow-[#4a90e2]/5' : 'bg-command-input/50 border-command-border hover:bg-command-input'}`} onClick={() => setIsChecked(!isChecked)} data-id="element-52">
                <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${isChecked ? 'bg-[#4a90e2] border-[#4a90e2]' : 'border-command-border-strong bg-command-app'}`} data-id="element-53">
                  {isChecked && <CheckIcon size={16} className="text-white" strokeWidth={3} data-id="element-54" />}
                </div>
                <span className={`flex-grow text-base font-medium transition-colors ${isChecked ? 'text-white' : 'text-command-text-muted'}`} data-id="element-55">
                  {confirmationText}
                </span>
              </label>
            </div>

            {/* Footer Actions */}
            <div className="p-8 pt-4 border-t border-command-border bg-command-app/50 flex justify-end gap-4" data-id="element-56">
              <Button variant="secondary" size="lg" onClick={onClose} className="rounded-xl px-8" data-id="element-57">
                Cancel
              </Button>
              <Button variant="primary" size="lg" disabled={!isChecked} onClick={onConfirm} className={`rounded-xl px-10 shadow-xl transition-all ${isChecked ? 'shadow-[#4a90e2]/20' : 'opacity-50'}`} data-id="element-58">
                Submit Official Report
              </Button>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
};